import type { ReactNode } from "react";

type SessionMetricProps = {
  label: string;
  value: string;
  tone?: "neutral" | "amber";
};

export function SessionMetric({ label, value, tone = "neutral" }: SessionMetricProps) {
  return (
    <div className="border-border/30 py-4 sm:px-4 sm:[&:not(:first-child)]:border-l">
      <p className="text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground/58">{label}</p>
      <p className={`mt-2 text-xl font-semibold tracking-[-0.04em] ${tone === "amber" ? "text-brand" : "text-foreground"}`}>
        {value}
      </p>
    </div>
  );
}

export type QualificationSectionStat = {
  id: string;
  index: string;
  label: string;
  count: number;
  completed: number;
};

type SessionSectionNavProps = {
  sections: QualificationSectionStat[];
  completedLabel: string;
  navigationLabel: string;
};

export function SessionSectionNav({ sections, completedLabel, navigationLabel }: SessionSectionNavProps) {
  return (
    <nav
      aria-label={navigationLabel}
      className="sticky top-0 z-10 -mx-3 border-b border-border/40 bg-background/95 px-3 py-3 backdrop-blur sm:-mx-5 sm:px-5 lg:-mx-6 lg:px-6"
    >
      <div className="grid gap-1 md:grid-cols-3">
        {sections.map((section) => (
          <a
            key={section.id}
            href={`#${section.id}`}
            className="group flex min-w-0 items-center justify-between gap-3 px-2 py-2 text-left transition-colors hover:bg-muted/30"
          >
            <span className="flex min-w-0 items-center gap-3">
              <span className="font-mono text-[0.65rem] text-brand">{section.index}</span>
              <span className="truncate text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground/75 group-hover:text-foreground">
                {section.label}
              </span>
            </span>
            <span className="shrink-0 text-xs text-muted-foreground/60">
              {section.completed}/{section.count} <span className="sr-only">{completedLabel}</span>
            </span>
          </a>
        ))}
      </div>
    </nav>
  );
}

type QualificationTestSectionProps = {
  id: string;
  index: string;
  title: string;
  count: number;
  completed: number;
  completedLabel: string;
  emptyMessage?: string;
  children: ReactNode;
};

export function QualificationTestSection({
  id,
  index,
  title,
  count,
  completed,
  completedLabel,
  emptyMessage,
  children,
}: QualificationTestSectionProps) {
  return (
    <section id={id} className="scroll-mt-20 border-b border-border/40 py-7 last:border-b-0 sm:py-9">
      <div className="mb-4 flex items-end justify-between gap-4 border-b border-border/30 pb-4">
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-xs text-brand">{index}</span>
          <h2 className="text-xl font-semibold tracking-tight text-foreground">{title}</h2>
        </div>
        <span className="shrink-0 text-xs text-muted-foreground/58">
          {completed}/{count} {completedLabel}
        </span>
      </div>
      {count === 0 && emptyMessage ? (
        <p className="rounded-lg border border-dashed border-border/35 py-6 text-sm leading-6 text-muted-foreground/66 px-4 text-center">{emptyMessage}</p>
      ) : (
        children
      )}
    </section>
  );
}
